import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, MessageCircle, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from './ui/button';
import LogoImage from './LogoImage';

const IG_URL = 'https://www.instagram.com/gifts_arguate/';

const InstagramButton = ({ size = 'h-10 w-10 sm:h-11 sm:w-11' }) => (
  <a
    href={IG_URL}
    target="_blank"
    rel="noreferrer"
    data-testid="floating-instagram"
    aria-label="Síguenos en Instagram"
    className={`insta-corner inline-flex ${size} rounded-full items-center justify-center flex-shrink-0`}
  >
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  </a>
);

const NAV = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'categorias', label: 'Categorías' },
  { id: 'productos', label: 'Productos' },
  { id: 'opiniones', label: 'Opiniones' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contacto', label: 'Contacto' },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { settings } = useSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const scrollTo = (id) => {
    if (!isHome) {
      // Cierra el menú y navega con hash; el scroll-padding-top se encarga del offset
      setMobileOpen(false);
      window.location.href = `/#${id}`;
      return;
    }
    const doScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (mobileOpen) {
      // Cierra primero el menú y espera a que termine la animación (~280ms)
      setMobileOpen(false);
      setTimeout(doScroll, 320);
    } else {
      doScroll();
    }
  };

  const waNumber = (settings.whatsapp_number || '').replace(/\D/g, '');

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-pinky-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 sm:gap-5 min-w-0">
          <InstagramButton />
          <Link to="/" className="relative flex items-center gap-2 sm:gap-3 min-w-0" data-testid="header-logo-link">
            {/* 1 sparkle decorativa — visible solo desde sm (≥640px) para mantener móvil limpio */}
            <Star
              aria-hidden="true"
              className="absolute -top-1.5 -right-2 h-3 w-3 text-amber-300 animate-twinkle pointer-events-none hidden sm:block"
              fill="currentColor"
              strokeWidth={0}
            />
            <LogoImage size="h-10 w-10 sm:h-12 sm:w-12" shape="circle" />
            <div className="leading-tight min-w-0">
              <p className="font-display text-xl sm:text-2xl text-pinky-600 tracking-tight truncate">Gifts <span className="text-lilac-400">GT</span></p>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold truncate">Entrega inmediata 🇬🇹</p>
            </div>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollTo(n.id)}
              className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-pinky-50 hover:text-pinky-600 transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2} />
            WhatsApp
          </a>

          <button
            onClick={() => setOpen(true)}
            data-testid="header-cart-btn"
            className="relative h-11 w-11 rounded-full bg-pinky-50 hover:bg-pinky-100 flex items-center justify-center text-pinky-600 transition-colors"
            aria-label="Carrito"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.8} />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-1 -right-1 bg-pinky-500 text-white text-xs font-bold h-5 min-w-[20px] px-1 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button
            className="lg:hidden h-11 w-11 rounded-full bg-pinky-50 flex items-center justify-center text-pinky-600"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="lg:hidden border-t border-pinky-100 bg-white overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => scrollTo(n.id)}
                  className="text-left px-4 py-3 rounded-xl text-slate-700 font-semibold hover:bg-pinky-50"
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
